import { Heading, Loading, Text } from '@iguanads/react'
import { useEffect, useState } from 'react'

import {
  THREE_DS_CARD_DATA,
  THREE_DS_READY,
  type ThreeDsCardDataMessageType,
} from '@/utils/three-ds-handshake'

const READY_RETRY_INTERVAL_MS = 300
const HANDOFF_TIMEOUT_MS = 20000

function submitGatewayForm(data: ThreeDsCardDataMessageType) {
  // Build and submit the gateway form via DOM APIs. Assigning input.value never
  // interprets HTML, so user-controlled values cannot inject markup or script
  // (unlike the previous document.write approach).
  const form = document.createElement('form')
  form.method = 'post'
  form.action = data.consumerUrl
  form.style.display = 'none'

  const fields: Record<string, string> = {
    card_number: data.cardNumber,
    card_expiry: data.cardExpiry,
    card_cvv: data.cardCvv,
  }

  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    form.appendChild(input)
  }

  document.body.appendChild(form)
  form.submit()
}

function Machine3ds() {
  const [missing, setMissing] = useState(false)

  useEffect(() => {
    const opener = window.opener as Window | null
    if (!opener) {
      setMissing(true)
      return
    }

    let settled = false

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return
      if (event.source !== opener) return
      if (event.data?.type !== THREE_DS_CARD_DATA) return

      const data = event.data as ThreeDsCardDataMessageType
      if (
        !data.consumerUrl ||
        !data.cardNumber ||
        !data.cardExpiry ||
        !data.cardCvv
      ) {
        return
      }

      settled = true
      cleanup()
      submitGatewayForm(data)
    }

    // Ask the opener for the card data, repeating until it answers (the opener's
    // tokenization may not have resolved yet when the popup mounts).
    const requestData = () => {
      opener.postMessage({ type: THREE_DS_READY }, window.location.origin)
    }

    const interval = window.setInterval(requestData, READY_RETRY_INTERVAL_MS)
    const timeout = window.setTimeout(() => {
      if (!settled) {
        cleanup()
        setMissing(true)
      }
    }, HANDOFF_TIMEOUT_MS)

    function cleanup() {
      window.clearInterval(interval)
      window.clearTimeout(timeout)
      window.removeEventListener('message', onMessage)
    }

    window.addEventListener('message', onMessage)
    requestData()

    return cleanup
  }, [])

  if (missing) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Heading>Missing parameters</Heading>
      </div>
    )
  }

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-2">
      <Loading size={5} /> <Text>Connecting...</Text>
    </div>
  )
}

export { Machine3ds }
